import type {
  AgentQueueItemWire,
  EscalationDeliveryState,
  StuckPrIdleBasis,
  StuckPrParkClass,
} from '@developerz/domain';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { idleFor, rescueCountdown } from '../lib/agent-queue';
import { ago, useMinuteClock } from '../lib/time';

// One STUCK PR on the escalation agent's queue drawer (#6338) — the wider face of the
// #6236 upcoming row it replaces. Presenter — a parked AI-managed pull request, not a
// task the agent holds: what it is parked ON (the park class, and the stored reason
// when the class cites one), how long it has sat there and on which clock, what babysit
// last did, whether a rescue round has been spent on it and when one hands over, and —
// honestly — whether the escalation it rides actually reached a human. Which model a
// rescue hand-over runs on is said once for the group, by `AgentQueueList`.

// Total over `StuckPrParkClass`, so a class the classifier learns is a compile error
// here until it gets a sentence.
const PARK_LABEL: Readonly<Record<StuckPrParkClass, string>> = {
  coding: 'agents.queue.park.coding',
  review: 'agents.queue.park.review',
  in_flight: 'agents.queue.park.in_flight',
  ci_infra: 'agents.queue.park.ci_infra',
  human_hold: 'agents.queue.park.human_hold',
  external_hold: 'agents.queue.park.external_hold',
  github_only: 'agents.queue.park.github_only',
  repo_config: 'agents.queue.park.repo_config',
  platform_fault: 'agents.queue.park.platform_fault',
  not_ai_managed: 'agents.queue.park.not_ai_managed',
  gate_edit_owner_applies: 'agents.queue.park.gate_edit_owner_applies',
  unobserved: 'agents.queue.park.unobserved',
  unobserved_head: 'agents.queue.park.unobserved_head',
  waiting: 'agents.queue.park.waiting',
  unclassified: 'agents.queue.park.unclassified',
};

// Total over `StuckPrIdleBasis` — the sub-line that says WHICH clock `idle_since_iso`
// is, because "idle 3d" means three different things across the three bases.
const IDLE_BASIS_LABEL: Readonly<Record<StuckPrIdleBasis, string>> = {
  head_first_seen: 'agents.queue.stuck.basis.head_first_seen',
  unobserved_head: 'agents.queue.stuck.basis.unobserved_head',
  unobserved: 'agents.queue.stuck.basis.unobserved',
};

// Total over `EscalationDeliveryState`. `collapsed` and `repeat` are labelled for what
// they are — nobody was emailed on THIS row — never as a delivery.
const DELIVERY_LABEL: Readonly<Record<EscalationDeliveryState, string>> = {
  pending: 'agents.queue.stuck.delivery.pending',
  delivered: 'agents.queue.stuck.delivery.delivered',
  collapsed: 'agents.queue.stuck.delivery.collapsed',
  no_route: 'agents.queue.stuck.delivery.no_route',
  send_failed: 'agents.queue.stuck.delivery.send_failed',
  digest_pending: 'agents.queue.stuck.delivery.digest_pending',
  unknown: 'agents.queue.stuck.delivery.unknown',
  repeat: 'agents.queue.stuck.delivery.repeat',
  bundle_pending: 'agents.queue.stuck.delivery.bundle_pending',
  opted_out: 'agents.queue.stuck.delivery.opted_out',
};

/** A stored escalation reason in words, when the catalog has them; the stored value
 * itself when it does not — never `⟦…⟧`, because the raw token is the next debugger's
 * only clue. */
const reasonLabel = (reason: string): string => {
  const key = `agents.queue.reason.${reason}`;
  return t.has(key) ? t(key) : reason;
};

/** The stuck arm of the wire — the only rows this component takes. */
type StuckRow = Extract<AgentQueueItemWire, { state: 'stuck' }>;

/**
 * One stuck row: repo and PR link, park class and reason, blocker token, idle (with its
 * basis), opened-at, last action, the rescue leg with its countdown, and the
 * escalation's delivery state. The clock labels read the shared minute clock
 * (`useMinuteClock`) while the row is mounted, so a drawer left open past a hand-over
 * says "Due" rather than the count it opened with (the queue read itself does not poll).
 */
export function AgentQueueStuckRow(props: { readonly row: StuckRow }): JSX.Element {
  const now = useMinuteClock();
  const row = props.row;
  return (
    <li class="agent-queue__row" data-park={row.park_class}>
      <p class="agent-queue__title">{row.title ?? t('agents.queue.stuck.untitled')}</p>
      <p class="agent-queue__meta">
        <span>{row.repo_full_name}</span>
        <a
          href={`https://github.com/${row.repo_full_name}/pull/${row.pr_number}`}
          target="_blank"
          rel="noreferrer"
        >
          {t('agents.queue.pr', { number: row.pr_number })}
        </a>
        <span>{t(PARK_LABEL[row.park_class])}</span>
        <Show when={row.park_reason}>{(reason) => <span>{reasonLabel(reason())}</span>}</Show>
        <span>{idleFor(row.idle_since_iso, now())}</span>
        <Show when={row.opened_at_iso}>
          {(iso) => <span>{t('agents.queue.stuck.opened', { ago: ago(iso(), now()) })}</span>}
        </Show>
        <Show when={row.last_action}>
          {(action) => (
            <span>
              {t('agents.queue.stuck.last_action', {
                executed: action().executed,
                ago: ago(action().at_iso, now()),
              })}
            </span>
          )}
        </Show>
      </p>
      <p class="agent-queue__hold">{t(IDLE_BASIS_LABEL[row.idle_basis])}</p>
      <Show when={row.blocker}>
        {(blocker) => (
          <p class="agent-queue__hold">{t('agents.queue.stuck.blocker', { blocker: blocker() })}</p>
        )}
      </Show>
      <p class="agent-queue__hold">{rescueLine(row)}</p>
      <Show when={row.escalation}>
        {(escalation) => (
          <p class="agent-queue__hold">
            {t('agents.queue.stuck.escalated', {
              ago: ago(escalation().created_at_iso, now()),
              delivery: t(DELIVERY_LABEL[escalation().delivery_state]),
            })}
          </p>
        )}
      </Show>
      <Show when={row.rescue_due_at_iso}>
        {(iso) => <p class="agent-queue__countdown">{rescueCountdown(iso(), now())}</p>}
      </Show>
    </li>
  );
}

/** The rescue leg in words: no round yet, one running, or the newest one and its end. */
function rescueLine(row: StuckRow): string {
  switch (row.rescue.state) {
    case 'none':
      return t('agents.queue.stuck.rescue_none');
    case 'live':
      return t('agents.queue.stuck.rescue_live', { reason: reasonLabel(row.rescue.reason) });
    case 'spent':
      return t('agents.queue.stuck.rescue_spent', {
        reason: reasonLabel(row.rescue.reason),
        outcome: row.rescue.outcome,
      });
  }
}
